import type { ButtonHTMLAttributes, CSSProperties, ReactNode } from "react";
import { forwardRef } from "react";
import { getSelectableRowStyle } from "../selectableRow.ts";
import {
  COLOR_ACTIVE_BG_SUBTLE,
  COLOR_BORDER,
  COLOR_DANGER,
  COLOR_TEXT_FAINT,
  COLOR_TEXT_SECONDARY,
  FONT_SIZE_BODY,
  RADIUS_SM,
} from "../tokens.ts";

export const MENU_DIVIDER_STYLE: CSSProperties = {
  height: 1,
  background: COLOR_BORDER,
  margin: "4px 6px",
};

const MENU_ROW_BASE_STYLE: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  width: "100%",
  padding: "6px 10px",
  border: "none",
  borderRadius: RADIUS_SM,
  background: "transparent",
  color: COLOR_TEXT_SECONDARY,
  fontSize: FONT_SIZE_BODY,
  textAlign: "left",
  cursor: "pointer",
  transition: "background 80ms",
};

export interface MenuButtonRowProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "style"> {
  "data-popover-item"?: boolean | "true";
  "data-popover-shortcut"?: string;
  active?: boolean;
  danger?: boolean;
  style?: CSSProperties;
  children: ReactNode;
}

export const MenuButtonRow = forwardRef<HTMLButtonElement, MenuButtonRowProps>(
  function MenuButtonRow(
    {
      active = false,
      danger = false,
      disabled = false,
      style,
      children,
      onMouseEnter,
      onMouseLeave,
      ...props
    },
    ref,
  ) {
    const { background: restingBackground } = getSelectableRowStyle(
      active,
      "subtle",
    );
    return (
      <button
        ref={ref}
        disabled={disabled}
        {...props}
        onMouseEnter={(e) => {
          if (!disabled)
            e.currentTarget.style.background = COLOR_ACTIVE_BG_SUBTLE;
          onMouseEnter?.(e);
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = restingBackground;
          onMouseLeave?.(e);
        }}
        style={{
          ...MENU_ROW_BASE_STYLE,
          background: restingBackground,
          color: disabled
            ? COLOR_TEXT_FAINT
            : danger
              ? COLOR_DANGER
              : COLOR_TEXT_SECONDARY,
          cursor: disabled ? "default" : "pointer",
          opacity: disabled ? 0.5 : 1,
          ...style,
        }}
      >
        {children}
      </button>
    );
  },
);

export function MenuDivider() {
  return <div style={MENU_DIVIDER_STYLE} />;
}
